import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://lovequetee.gallery";
const SHARE_IMAGE = "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/4vjglkda_2837.webp";

const DEFAULT_METADATA = {
  title: "Love Quetee — Fine Artist & Contemporary Painter",
  description: "Explore Love Quetee’s paintings, exhibitions, and collector archive shaped by belonging, cultural memory, and the living world.",
  robots: "index, follow",
};

const ROUTE_METADATA = {
  "/": DEFAULT_METADATA,
  "/works": {
    title: "Works — Love Quetee Fine Art",
    description: "Move through Love Quetee’s paintings, still life studies, and archival print editions.",
    robots: "index, follow",
  },
  "/pricelist": {
    title: "Price List — Love Quetee",
    description: "View the Descendants of the Sun original artwork price list by Love Quetee.",
    robots: "index, follow",
  },
  "/search": {
    title: "Search the Love Quetee Archive",
    description: "Find Love Quetee’s public paintings, exhibitions, print editions, and studio pages.",
    robots: "index, follow",
  },
  "/about": {
    title: "About Love Quetee — Fine Artist",
    description: "Meet Love Quetee and explore a painting practice rooted in Black identity, cultural memory, and the natural world.",
    robots: "index, follow",
  },
  "/exhibitions": {
    title: "Exhibitions — Love Quetee",
    description: "View current, upcoming, and past exhibitions carrying Love Quetee’s paintings beyond the studio.",
    robots: "index, follow",
  },
  "/news-events": {
    title: "News & Events — Love Quetee",
    description: "Follow Love Quetee’s upcoming shows and studio events, beginning with Descendants of the Sun in Saint Paul.",
    robots: "index, follow",
  },
  "/contact": {
    title: "Contact the Love Quetee Studio",
    description: "Begin a considered conversation about Love Quetee’s paintings, commissions, exhibitions, or studio visits.",
    robots: "index, follow",
  },
  "/studio": {
    title: "Private Studio — Love Quetee",
    description: "Private Love Quetee studio dispatch desk.",
    robots: "noindex, nofollow",
  },
  "/payment/success": {
    title: "Print Edition Confirmed — Love Quetee",
    description: "Your Love Quetee print edition checkout is being confirmed.",
    robots: "noindex, nofollow",
  },
  "/payment/cancel": {
    title: "Checkout Paused — Love Quetee",
    description: "Your Love Quetee print edition checkout was paused.",
    robots: "noindex, nofollow",
  },
};

const EXHIBITION_METADATA = {
  "minnesota-state-fair": {
    title: "Minnesota State Fair — Love Quetee",
    description: "Explore Love Quetee’s 2026 juried Minnesota State Fair exhibition and selected works.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/dy7zzs6m_Screenshot_20260902_191011_Gallery.jpg",
    imageAlt: "Installation view of Love Quetee fine art exhibition at the Minnesota State Fair",
  },
  "descendants-of-the-sun": {
    title: "Descendants of the Sun — Love Quetee",
    description: "Discover Love Quetee’s upcoming Descendants of the Sun exhibition in Saint Paul, Minnesota.",
    image: SHARE_IMAGE,
    imageAlt: "Love Quetee oil painting Gbanganla from the Descendants of the Sun series",
  },
  "ghetto-visionary": {
    title: "Ghetto Visionary — Love Quetee",
    description: "Visit the Ghetto Visionary exhibition archive and installation record from The 410 Project.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/coz03gst_Screenshot_20260902_185951_Instagram.jpg",
    imageAlt: "Installation view of visitors and artworks at Love Quetee Ghetto Visionary exhibition",
  },
};

function metadataForPath(pathname) {
  if (ROUTE_METADATA[pathname]) return ROUTE_METADATA[pathname];
  if (pathname.startsWith("/exhibitions/")) {
    const slug = pathname.slice("/exhibitions/".length);
    return { ...(EXHIBITION_METADATA[slug] || DEFAULT_METADATA), robots: "index, follow" };
  }
  return DEFAULT_METADATA;
}

function setMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(url) {
  let element = document.head.querySelector("link[rel=\"canonical\"]");
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", url);
}

export function SiteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const metadata = metadataForPath(pathname);
    const canonicalUrl = new URL(pathname || "/", SITE_URL).toString();
    const shareImage = metadata.image || SHARE_IMAGE;
    const shareImageAlt = metadata.imageAlt || "Love Quetee artwork from the luminous botanical archive";
    document.title = metadata.title;
    setMeta("name", "description", metadata.description);
    setMeta("name", "robots", metadata.robots);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Love Quetee");
    setMeta("property", "og:title", metadata.title);
    setMeta("property", "og:description", metadata.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", shareImage);
    setMeta("property", "og:image:alt", shareImageAlt);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", metadata.title);
    setMeta("name", "twitter:description", metadata.description);
    setMeta("name", "twitter:image", shareImage);
    setCanonical(canonicalUrl);
  }, [pathname]);

  return null;
}
